import { Link } from 'react-router-dom';

export interface HeroCTA {
  label: string;
  href: string;
  primary?: boolean;
  external?: boolean;
}

export interface PageHeroProps {
  badge?: string;
  headline: string;
  headlineItalic?: string;
  subheadline: string;
  ctas: HeroCTA[];
  imageSrc: string;
  urgency?: string;
}

export default function PageHero({
  badge,
  headline,
  headlineItalic,
  subheadline,
  ctas,
  imageSrc,
  urgency,
}: PageHeroProps) {
  return (
    <section className="relative w-full min-h-screen flex items-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={imageSrc}
          alt="Yarnell Emporium boutique interior"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-inkblack/92 via-inkblack/65 to-inkblack/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-inkblack/80 via-transparent to-inkblack/35" />
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-inkblack to-transparent" />
      </div>

      {/* Decorative vertical rule */}
      <div className="absolute left-8 lg:left-16 top-1/2 -translate-y-1/2 w-px h-52 bg-antique/25 hidden lg:block" />

      {/* Content — static, no animation */}
      <div className="relative z-10 w-full px-8 lg:px-20 pb-28 pt-44">
        <div className="max-w-screen-xl mx-auto">
          <div className="max-w-3xl">
            {badge && (
              <div className="mb-8 flex items-center gap-4">
                <span className="w-12 h-px bg-antique" />
                <span className="text-antique text-xs font-sans font-500 tracking-[0.28em] uppercase">{badge}</span>
              </div>
            )}

            <h1 className="font-serif text-white mb-6">
              <span
                className="block"
                style={{
                  fontSize: 'clamp(3rem, 7vw, 6.5rem)',
                  lineHeight: '1.02',
                  letterSpacing: '-0.025em',
                  fontWeight: 700,
                }}
              >
                {headline}
              </span>
              {headlineItalic && (
                <span
                  className="block italic text-antique"
                  style={{
                    fontSize: 'clamp(2.6rem, 6vw, 5.5rem)',
                    lineHeight: '1.08',
                    fontWeight: 400,
                  }}
                >
                  {headlineItalic}
                </span>
              )}
            </h1>

            {/* Ornamental rule */}
            <div className="flex items-center gap-4 mb-7">
              <span className="w-16 h-px bg-antique/50" />
              <span className="w-2 h-2 rotate-45 border border-antique/50 flex-shrink-0" />
              <span className="w-8 h-px bg-antique/30" />
            </div>

            <p className="text-white/65 text-base lg:text-lg font-sans font-300 leading-relaxed mb-8 max-w-lg">
              {subheadline}
            </p>

            {urgency && (
              <div className="flex items-center gap-3 mb-9">
                <span className="w-2 h-2 rotate-45 border border-antique/70 flex-shrink-0" />
                <span className="text-antique/90 text-xs font-sans font-500 tracking-[0.14em]">{urgency}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              {ctas.map((cta, i) =>
                cta.external ? (
                  <a
                    key={i}
                    href={cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs font-sans font-600 tracking-[0.16em] uppercase px-10 py-4 whitespace-nowrap cursor-pointer ${
                      cta.primary
                        ? 'bg-antique text-inkblack hover:bg-white hover:text-inkblack'
                        : 'border border-white/45 text-white hover:border-antique hover:text-antique'
                    }`}
                  >
                    {cta.label}
                  </a>
                ) : (
                  <Link
                    key={i}
                    to={cta.href}
                    className={`text-xs font-sans font-600 tracking-[0.16em] uppercase px-10 py-4 whitespace-nowrap cursor-pointer ${
                      cta.primary
                        ? 'bg-antique text-inkblack hover:bg-white hover:text-inkblack'
                        : 'border border-white/45 text-white hover:border-antique hover:text-antique'
                    }`}
                  >
                    {cta.label}
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom info strip — static */}
      <div className="absolute bottom-0 right-0 hidden lg:flex items-center gap-0 z-20">
        <div className="bg-antique px-6 py-4 flex items-center gap-3">
          <i className="ri-time-line text-inkblack text-sm" />
          <span className="text-inkblack text-xs font-sans font-600 tracking-[0.14em] uppercase whitespace-nowrap">Open Daily 9–5</span>
        </div>
        <div className="bg-inkblack border border-white/10 px-6 py-4 flex items-center gap-3">
          <i className="ri-map-pin-line text-antique text-sm" />
          <span className="text-white/70 text-xs font-sans tracking-wide whitespace-nowrap">Yarnell, AZ · Hwy 89</span>
        </div>
        <a
          href="tel:9284279593"
          className="bg-charcoal border border-white/10 px-6 py-4 flex items-center gap-3 hover:bg-antique/15 cursor-pointer"
        >
          <i className="ri-phone-line text-antique text-sm" />
          <span className="text-white/70 text-xs font-sans tracking-wide whitespace-nowrap">928.427-9593</span>
        </a>
      </div>
    </section>
  );
}
